// src/pages/common-components/Auth/Signup.tsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../Contexts/AuthContext';
import { AuthCard, AuthError, authInputStyle, authLabelStyle, authSubmitStyle, friendlyAuthError } from './AuthShared';

const Signup: React.FC = () => {
  const { signup, logout } = useAuth();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fullName.trim() || !email.trim() || !password) {
      setError('Please fill in your name, email, and password.');
      return;
    }
    if (password.length < 6) {
      setError('Password should be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await signup(fullName.trim(), email.trim(), password, phone.trim() || undefined);
      // createUserWithEmailAndPassword signs the new account in automatically —
      // sign it back out so the user actually has to sign in, matching the
      // "create account, then sign in" flow of a real auth experience.
      await logout();
      navigate('/login', { replace: true, state: { justSignedUp: true } });
    } catch (err: any) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard title="Create Account" subtitle="Sign up to save favourites, book test drives, and track reservations">
      <form onSubmit={handleSubmit}>
        <AuthError message={error} />

        <div style={{ marginBottom: 18 }}>
          <label style={authLabelStyle}>Full Name</label>
          <input
            type="text"
            style={authInputStyle}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="John Doe"
            autoComplete="name"
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={authLabelStyle}>Email Address</label>
          <input
            type="email"
            style={authInputStyle}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={authLabelStyle}>Phone Number <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span></label>
          <input
            type="tel"
            style={authInputStyle}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+94 77 123 4567"
            autoComplete="tel"
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={authLabelStyle}>Password</label>
          <input
            type="password"
            style={authInputStyle}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            autoComplete="new-password"
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <label style={authLabelStyle}>Confirm Password</label>
          <input
            type="password"
            style={authInputStyle}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter your password"
            autoComplete="new-password"
          />
        </div>

        <button type="submit" style={{ ...authSubmitStyle, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
          {submitting ? 'Creating Account…' : 'Create Account'}
        </button>

        <p style={{ textAlign: 'center', fontSize: 14, color: '#666', marginTop: 22, marginBottom: 0 }}>
          Already have an account? <Link to="/login" style={{ color: '#717fe0', fontWeight: 600 }}>Sign In</Link>
        </p>
      </form>
    </AuthCard>
  );
};

export default Signup;
